import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Activity, Search, Eye, Edit, Trash2, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

import { BACKEND_URL } from '@/lib/backend';
export default function ActivityLogsModule() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { loadLogs(); }, [entityTypeFilter]);

  const getFilterValue = () => entityTypeFilter === 'all' ? '' : entityTypeFilter;

  const loadLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = new URL(`${BACKEND_URL}/api/documentation/activity-logs`);
      const filterValue = entityTypeFilter === 'all' ? '' : entityTypeFilter;
      if (filterValue) url.searchParams.append('entity_type', filterValue);
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) setLogs(await response.json());
    } catch (err) {
      console.error('Failed to load logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch(action) {
      case 'created': return <Plus className="w-4 h-4 text-green-600" />;
      case 'updated': return <Edit className="w-4 h-4 text-blue-600" />;
      case 'deleted': return <Trash2 className="w-4 h-4 text-red-600" />;
      case 'viewed': return <Eye className="w-4 h-4 text-slate-600" />;
      default: return <Activity className="w-4 h-4 text-slate-600" />;
    }
  };

  const getActionColor = (action) => {
    switch(action) {
      case 'created': return 'bg-green-100 text-green-700';
      case 'updated': return 'bg-blue-100 text-blue-700';
      case 'deleted': return 'bg-red-100 text-red-700';
      case 'viewed': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const filtered = logs.filter(log =>
    log.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.entity_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.details?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto bg-slate-50">
      <div className="mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Activity Logs</h1>
          <p className="text-sm text-slate-500 mt-1">Audit trail of all documentation changes</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input 
                placeholder="Search logs..." 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)} 
                className="pl-10" 
              />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by entity type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="password">Password</SelectItem>
                <SelectItem value="device">Device</SelectItem>
                <SelectItem value="domain">Domain</SelectItem>
                <SelectItem value="document">Document</SelectItem>
                <SelectItem value="network_diagram">Network Diagram</SelectItem>
                <SelectItem value="attachment">Attachment</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="text-center py-12"><p className="text-slate-500">Loading logs...</p></div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Activity className="w-12 h-12 mx-auto text-slate-400 mb-3" />
            <p className="text-slate-600 font-medium">No activity logs found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((log) => (
            <Card key={log.id} className="hover:shadow-sm transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-1">{getActionIcon(log.action)}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-slate-800">{log.user_name}</span>
                      <Badge className={`text-xs ${getActionColor(log.action)}`}>{log.action}</Badge>
                      <Badge variant="outline" className="text-xs">{log.entity_type}</Badge>
                    </div>
                    {log.details && (
                      <p className="text-sm text-slate-600 mb-1">{log.details}</p>
                    )}
                    <p className="text-xs text-slate-400">
                      {new Date(log.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
